<template>
    <el-dialog 
        title="Historical lookup" 
        :visible="visible"
        width="90%"
        @update:visible="$emit('update:visible', $event)"
        class="video-search-dialog"
    >
        <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
            :disabled="loading">
        </el-date-picker>
        <el-table 
            :data="tableData"
            :default-sort="{prop: 'startTime', order: 'ascending'}"
            v-loading="loading"
            row-key="id"
            height="600"
            @selection-change="onSelectionChange"
            :expand-row-keys="selectedIds"
            ref="table"
        >
            <el-table-column type="selection" width="55"></el-table-column>
            <el-table-column 
                prop="channelName" 
                label="Channel"
                :filters="channelFilters"
                :filter-method="filterByChannel"
            >
            </el-table-column>
            <el-table-column prop="startTime" label="Start Time" sortable>
                <template v-slot="{row: {id, startTime}}">
                    {{ startTime.format('YYYY-MM-DD HH:mm:ss') }}
                </template>
            </el-table-column>
            <el-table-column prop="endTime" label="End Time" sortable>
                <template v-slot="{row: {id, endTime}}">
                    {{ endTime.format('YYYY-MM-DD HH:mm:ss') }}
                </template>
            </el-table-column>
            <el-table-column type="expand">
                <template v-slot="{row: {id, startTime, endTime}}">
                    <template v-if="selected[id]">
                        <TimeRangeSlider v-model="selected[id].timeRange" :min="startTime" :max="endTime" :step="10"/>
                    </template>
                </template>
            </el-table-column>
        </el-table>
        <div slot="footer" class="dialog-footer">
            <el-button 
                type="primary" 
                :disabled="downloadDisabled" 
                :loading="downloading"
                @click="download"
            >
                Download
            </el-button>
        </div>
    </el-dialog>
</template>

<style lang="scss">  
    .video-search-dialog {            
        .el-table__expand-column .cell {
            display: none;
        }
    }
</style>

<script>
import {videoClient} from '@api/video.js'
import moment from 'moment';
import TimeRangeSlider from '@components/common/TimeRangeSlider';

export default {
    components: {
        TimeRangeSlider
    },
    props: ['device', 'visible'],
    data() {
        return {
            dateRange: null,
            loading: false,
            downloading: false,
            searchResult: [],
            selected: {}
        }
    },
    computed: {
        tableData() { 
            return this.searchResult.map(({id, channel, startTime, endTime}) => ({
                id,
                channel: channel,
                channelName: (this.device.channels[channel] || {name: `Index ${channel}`}).name,
                startTime: moment(startTime),
                endTime: moment(endTime)
            }))
        },
        downloadDisabled() {
            const loading = this.loading;
            const downloading = this.downloading;
            const selectedLength = Object.keys(this.selected).length;
            if(loading || downloading || !selectedLength) return true;
            const timeRanges = Object.keys(this.selected)
                .map(id => this.selected[id].timeRange)
                .filter(x => !!x);
            return timeRanges.length !== selectedLength || timeRanges.some(x => x[0].isSameOrAfter(x[1]));
        },
        selectedIds() {
            return Object.keys(this.selected);
        },
        channelFilters() {
            return (this.device || {channels: []}).channels.map(({name, channelIndex}) => ({
                text: name,
                value: channelIndex
            }))
        }
    },
    watch: {
        device() {
            this.loading = false;
            this.dateRange = null;
            this.search();
        },
        dateRange() {
            this.search();
        }
    },
    methods: {
        search() {
            if(!this.dateRange) {
                this.searchResult = [];
                return;
            }
            this.loading = true;
            const serial = this.device.serial; 
            const from = moment(this.dateRange[0]);
            const to =  moment(this.dateRange[1]);
            videoClient.search(serial, from, to)
                .then(({data: {files}}) => {
                    if(serial !== this.device.serial) return;
                    this.loading = false;
                    this.searchResult = files;
                })
                .catch(() => {
                    if(serial !== this.device.serial) return;
                    this.loading = false;
                    this.searchResult = [];
                    this.$notify.error({
                        title: 'Error',
                        message: 'Unable to load historical lookup result'
                    });
                });
        },
        download() {
            const serial = this.device.serial; 
            const files = Object.keys(this.selected).map(id => ({
                id,
                from: this.selected[id].timeRange[0],
                to: this.selected[id].timeRange[1]
            }));
            this.downloading = true;
            videoClient.download(serial, files)
                .then(({data: {status}}) => {
                    if(serial !== this.device.serial) return;
                    this.downloading = false;
                    if(status === 'FAIL') {
                        this.$notify.error({
                            title: 'Error',
                            message: 'Unable to download requested files'
                        });
                    } else {
                        this.$notify.success({
                            title: 'Success',
                            message: 'The files download request completed. Click on this message to watch the downloading proggress',
                            customClass: 'download-success-notification',
                            onClick: () => this.$router.push({ name: "viewDevice", params: { serial } })
                        });
                    }
                })
                .catch(error => {
                    if(serial !== this.device.serial) return;
                    this.downloading = false;
                    if(error.response) {
                        const status = error.response.status;
                        if(status === 503) {
                            this.$notify.error({
                                title: 'Error',
                                message: 'Unable to download requested files because the device unavailable'
                            });
                            return;
                        }
                    }
                    this.$notify.error({
                        title: 'Error',
                        message: 'Unable to download requested files'
                    });
                });
        },
        onSelectionChange(rows) {
            const selectedIds = rows.reduce((obj, row) => {
                obj[row.id] = row;
                return obj;
            }, {});
            Object.keys(this.selected).forEach(id => {
                if(!selectedIds[id]) {
                    this.$delete(this.selected, id);
                }
            });
            Object.keys(selectedIds).forEach(id => {
                if(!this.selected[id]) {
                    const {startTime, endTime} = selectedIds[id];
                    this.$set(this.selected, id, {
                        timeRange: null
                    });
                }
            });
        },
        filterByChannel(value, {channel}) {
            return channel === value
        }
    }
}
</script>


// WEBPACK FOOTER //
// VideoSearchDialog.vue?3779e422